import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, ExternalLink, Linkedin, Check, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";

export default function LinkedInModal({ isOpen, onClose, job }) {
    const [loading, setLoading] = useState(true);
    const [postContent, setPostContent] = useState("");
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (isOpen && job) {
            fetchPost();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, job]);

    const fetchPost = async () => {
        setLoading(true);
        try {
            const customInstructions = localStorage.getItem('linkedin_custom_instructions');
            const res = await axios.post(`${API_BASE}/jobs/${job._id}/net/linkedin`, {
                custom_instructions: customInstructions
            });
            setPostContent(res.data.content);
        } catch (err) {
            console.error(err);
            toast.error("Failed to generate post");
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const handleCopyAndOpen = () => {
        navigator.clipboard.writeText(postContent);
        setCopied(true);
        toast.success("Copied to clipboard!");

        setTimeout(() => {
            window.open("https://www.linkedin.com/feed/?shareActive=true", "_blank");
        }, 800);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.95, y: 20 }}
                        className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-2xl shadow-xl overflow-hidden border border-slate-200 dark:border-slate-800"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="bg-[#0077b5] p-6 text-white flex justify-between items-start">
                            <div>
                                <h3 className="text-xl font-bold flex items-center gap-2">
                                    <Linkedin className="w-6 h-6" /> LinkedIn Broadcaster
                                </h3>
                                <p className="text-blue-100 text-sm mt-1">AI-generated viral post for "{job?.title}"</p>
                            </div>
                            <button onClick={onClose} className="text-blue-100 hover:text-white transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-6">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                                    <Loader2 className="w-10 h-10 text-[#0077b5] animate-spin" />
                                    <p className="text-slate-500 text-sm animate-pulse">Drafting viral content...</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="relative">
                                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                            Generated Post Preview
                                        </label>
                                        <textarea
                                            value={postContent}
                                            onChange={(e) => setPostContent(e.target.value)}
                                            className="w-full h-64 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 text-sm font-medium focus:ring-2 focus:ring-[#0077b5] outline-none resize-none leading-relaxed"
                                        />
                                    </div>

                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg flex gap-3 items-start">
                                        <div className="p-2 bg-blue-100 dark:bg-blue-800 text-[#0077b5] dark:text-blue-200 rounded-full">
                                            <ExternalLink className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-bold text-[#0077b5] dark:text-blue-300">Smart Action</h4>
                                            <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                                                Clicking below will <b>copy</b> this text and <b>open</b> LinkedIn's "New Post" window instantly.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {!loading && (
                            <div className="p-6 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex justify-end">
                                <button
                                    onClick={handleCopyAndOpen}
                                    className="w-full py-3 bg-[#0077b5] hover:bg-[#006097] text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-200 dark:shadow-none hover:scale-[1.02] active:scale-[0.98]"
                                >
                                    {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                                    {copied ? "Copied! Opening LinkedIn..." : "Copy & Open LinkedIn"}
                                </button>
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
